import React from 'react';
import { useTranslation } from 'react-i18next';
import { XCircle, RefreshCw, RotateCcw } from 'lucide-react';
import { cn } from '../../lib/utils';

const variants = {
  default: 'bg-muted text-muted-foreground border-border',
  success: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  destructive: 'bg-red-500/10 text-red-500 border-red-500/20',
  warning: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  primary: 'bg-primary/10 text-primary border-primary/20',
  indigo: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20',
  purple: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
  blue: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
};

export default function StatusBadge({ 
  status,
  children, 
  variant: manualVariant, 
  icon: Icon,
  className,
  dot = false
}) {
  const { t } = useTranslation();
  const getStatusConfig = (s) => {
    switch (s) {
      case 'COMPLETED':
        return { variant: 'success', label: t('status.completed') };
      case 'ACTIVE':
        return { variant: 'success', label: t('status.active') };
      case 'FAILED':
        return { variant: 'destructive', label: t('status.failed') };
      case 'RETRY_EXHAUSTED':
        return { variant: 'purple', label: t('status.retryExhausted') };
      case 'PREEMPTED':
        return { variant: 'blue', label: t('status.preempted') };
      case 'CANCELED':
        return { variant: 'default', label: t('status.canceled'), icon: XCircle };
      case 'PAUSED':
        return { variant: 'indigo', label: t('status.paused') };
      case 'WAITING':
        return { variant: 'warning', label: t('status.waiting') };
      case 'RUNNING':
        return { variant: 'primary', label: t('status.running') };
      case 'PENDING':
        return { variant: 'blue', label: t('status.pending') };
      case 'APPROVED':
        return { variant: 'success', label: t('status.approved') };
      case 'REJECTED':
        return { variant: 'destructive', label: t('status.rejected') };
      case 'EXECUTED':
        return { variant: 'indigo', label: t('status.executed') };
      case 'EXECUTING':
        return { variant: 'primary', label: t('status.executing'), icon: RefreshCw };
      case 'DRAFT':
        return { variant: 'default', label: t('status.draft') };
      case 'RESTART_INITIATED':
        return { variant: 'indigo', label: t('status.fullReset'), icon: RefreshCw };
      case 'CASCADE_RESET':
        return { variant: 'warning', label: t('status.recoveryReset'), icon: RotateCcw };
      default:
        return { variant: 'default', label: s ? t(`status.${String(s).toLowerCase()}`, { defaultValue: s }) : children };
    }
  };

  const config = status ? getStatusConfig(status) : { variant: manualVariant || 'default', label: children };

  return (
    <span className={cn(
      "inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest rounded-lg border shadow-sm transition-all",
      variants[config.variant] || variants.default,
      className
    )}>
      {dot && <span className={cn("w-1 h-1 rounded-full animate-pulse mr-0.5", 
        config.variant === 'success' ? 'bg-emerald-500' : 
        config.variant === 'destructive' ? 'bg-red-500' : 
        config.variant === 'warning' ? 'bg-amber-500' : 
        'bg-current'
      )} />}
      {(Icon || config.icon) && (
        <span className="shrink-0">
          {Icon ? <Icon className="w-3 h-3" /> : React.createElement(config.icon, { className: "w-3 h-3" })}
        </span>
      )}
      {config.label}
    </span>
  );
}
